import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Page from "src/layouts/Page";
import ContentTable, {
	ContentTableItem
} from "src/components/ContentTable/ContentTable";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import useActivePromotions from "../../hooks/useActivePromotions";
import useDeletePromotionBanners from "../../hooks/useDeletePromotionBanners";
import useMakePromotionBannersActive from "../../hooks/useMakePromotionBannersActive";
import useMakePromotionBannersInactive from "../../hooks/useMakePromotionInactive";
import useMovePromotionBanner from "../../hooks/useMovePromotionBanner";
import usePromotionBannerLibrary from "../../hooks/usePromotionBannerLibrary";

function mapBannersToContentTableItems(
	activeBanners: Banner[],
	libraryBanners: Banner[]
): ContentTableItem[] {
	const live = activeBanners.map((b, index) => ({
		id: b.id,
		name: b.name,
		createdBy: b.createdBy
			? `${b.createdBy.firstName} ${b.createdBy.lastName}`
			: undefined,
		lastUpdated: b.updatedAt,
		isActive: true,
		order: index + 1
	}));

	const draft = libraryBanners.map((b) => ({
		id: b.id,
		name: b.name,
		createdBy: b.createdBy
			? `${b.createdBy.firstName} ${b.createdBy.lastName}`
			: undefined,
		lastUpdated: b.updatedAt ?? b.createdAt,
		isActive: false
	}));

	return [...live, ...draft];
}

const Promotion = () => {
	const navigate = useNavigate();
	const [activeBanners, setActiveBanners] = useState<Banner[]>([]);
	const [bannerLibrary, setBannerLibrary] = useState<Banner[]>([]);
	const [itemToDelete, setItemToDelete] = useState<ContentTableItem | null>(
		null
	);

	usePromotionBannerLibrary({ onSuccess: setBannerLibrary });
	useActivePromotions({ onSuccess: setActiveBanners });

	const { mutate: makeBannersActive } = useMakePromotionBannersActive();
	const { mutate: deleteCarouselBanners, isLoading: isDeletingBanners } =
		useDeletePromotionBanners();
	const { mutate: movePromotionBanner } = useMovePromotionBanner();
	const { mutate: makeBannersInactive } = useMakePromotionBannersInactive();

	const items = mapBannersToContentTableItems(activeBanners, bannerLibrary);

	const handleMove = (item: ContentTableItem, direction: -1 | 1) => {
		movePromotionBanner({ id: Number(item.id), offset: direction });
		setActiveBanners((prev) => {
			const index = prev.findIndex((b) => b.id === Number(item.id));
			if (index < 0) return prev;
			const newIndex = index + direction;
			if (newIndex < 0 || newIndex >= prev.length) return prev;
			const next = [...prev];
			[next[index], next[newIndex]] = [next[newIndex], next[index]];
			return next;
		});
	};

	return (
		<Page>
			<ContentTable
				items={items}
				onRowClick={(item) =>
					navigate(
						`/vendor-sponsorship/promotion/banners/new-promotion-form/${item.id}`
					)
				}
				onMakeLive={(item) => makeBannersActive([Number(item.id)])}
				onMakeDraft={(item) => makeBannersInactive([Number(item.id)])}
				onDelete={(item) => setItemToDelete(item)}
				canDelete={(item) => !item.isActive}
				onMoveUp={(item) => handleMove(item, -1)}
				onMoveDown={(item) => handleMove(item, 1)}
				createPath="/vendor-sponsorship/promotion/banners/new-promotion-form"
				createLabel="Create Banner"
			/>

			<ConfirmationModal
				isOpen={itemToDelete !== null}
				title="Delete banner?"
				description="This action cannot be undone."
				onCancel={() => setItemToDelete(null)}
				onSubmit={() => {
					if (itemToDelete) {
						deleteCarouselBanners([Number(itemToDelete.id)]);
						setItemToDelete(null);
					}
				}}
				submitBtnProps={{ isLoading: isDeletingBanners }}
			/>
		</Page>
	);
};

export default Promotion;
